/**
 * Pull request reviews (`/reviews`, `/reviews/:prStateId`): every pull
 * request Proctor watches on the repositories the reader can see.
 *
 *   header   "Pull request reviews", how many repositories Proctor reads and
 *            how many are still on the Reviews team; Move repositories onto
 *            Teams (while any are), Review a pull request, the gear
 *   chips    the Reviews team's review line (On GitHub, Level),
 *            the search box, Status, Repository, Author; "<n> reviews today"
 *   sections Held for you (five a page, the longest wait first) and All pull
 *            requests (twenty a page, the newest activity first), each with
 *            its own pager
 *
 * `/reviews/:prStateId` is the list with that review open in the sheet. The
 * search, the chips and both pages live in the URL (`q`, `status`, `repo`,
 * `author`, `page`, `held`), so a link opens the same view.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type CSSProperties } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Search, Settings } from "lucide-react";
import { DEFAULT_JOB_INSTANCE, type Job, type RepoClaimRow, type ReviewLevel } from "@yagni/shared";
import { useAuthSession } from "../hooks/useWorkspaceRole.js";
import {
  Btn,
  ChevronDownIcon,
  Chip,
  Group,
  Groups,
  ListHead,
  MenuItem,
  MoreMenu,
  Pager,
  Row,
} from "../queue/index.js";
import { useOwnedSheet } from "../shell/useOwnedSheet.js";
import { useNavDesks } from "../shell/useNavDesks.js";
import { fetchTeamJobs } from "../teams/teamsApi.js";
import { captureUiError } from "../sentry.js";
import { reviewsApi, type ReviewsApi } from "./reviewsApi.js";
import { useClampPages, useReviewsQuery, useSearchDraft } from "./useReviewsQuery.js";
import { invalidateReviews, readReviews, useReviewsList } from "./useReviewsList.js";
import {
  REVIEW_STATUS_MENU,
  listRowState,
  repoName,
  repoOwnershipOf,
  reviewAge,
  reviewStateLine,
  type RepoOwnership,
  type ReviewRow,
} from "./reviewsModel.js";
import { ReviewSheet } from "./ReviewSheet.js";
import { NewReviewSheet } from "./NewReviewSheet.js";
import { MoveReposSheet } from "./MoveReposSheet.js";

const LEVEL_WORD: Record<ReviewLevel, string> = {
  light: "Light",
  balanced: "Balanced",
  tough: "Tough",
};

function lineOf(jobs: readonly Job[], kind: string): Job | null {
  const lines = jobs.filter((job) => job.kind === kind && job.active);
  return lines.find((job) => job.instance === DEFAULT_JOB_INSTANCE) ?? lines[0] ?? null;
}

/** The header's second fact: who owns the repositories, or why it cannot say. */
export function ownershipSentence(
  claims: readonly RepoClaimRow[] | null,
  claimsFailed: boolean,
  ownership: RepoOwnership,
): string {
  if (claims == null)
    return claimsFailed ? "Could not read which Team owns which repository just now" : "";
  if (ownership.unowned.length === 0)
    return "Every repository is owned by a Team. The Reviews team has dissolved.";
  return `${ownership.owned} owned by a Team and tuned there · ${ownership.unowned.length} still on the Reviews team, tuned by nobody`;
}

/** A section's empty line: still reading, nothing matches what the reader asked, or nothing at all. */
function emptyLine(
  loading: boolean,
  filtered: boolean,
  lines: { filtered: string; none: string },
): string {
  if (loading) return "Reading the reviews.";
  return filtered ? lines.filtered : lines.none;
}

export interface ReviewsPageProps {
  api?: ReviewsApi;
  now?: number;
}

export function ReviewsPage({ api = reviewsApi, now: fixedNow }: ReviewsPageProps) {
  const { prStateId } = useParams<{ prStateId?: string }>();
  const navigate = useNavigate();
  const session = useAuthSession();
  const nav = useNavDesks(session?.user.id ?? "");
  const desks = useMemo(
    () => (nav.isLoading ? null : nav.desks.map((desk) => ({ id: desk.id, name: desk.name }))),
    [nav],
  );
  const now = fixedNow ?? Date.now();

  const { query, searchParams, setParams } = useReviewsQuery();
  const list = useReviewsList(query, desks, api);
  const [draft, setDraft] = useSearchDraft(query.q, setParams);
  useClampPages(list, query, setParams);

  const [claims, setClaims] = useState<RepoClaimRow[] | null>(null);
  const [claimsFailed, setClaimsFailed] = useState(false);
  const [claimsVersion, setClaimsVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    api
      .repoClaims()
      .then((body) => {
        if (cancelled) return;
        setClaims(body.claims);
        setClaimsFailed(false);
      })
      .catch((err: unknown) => {
        captureUiError(err, {
          area: "Reviews",
          operation: "read_repo_claims",
          severity: "warning",
        });
        if (!cancelled) setClaimsFailed(true);
      });
    return () => {
      cancelled = true;
    };
  }, [api, claimsVersion]);

  const catchAllId = list.teams.catchAll[0] ?? null;
  const [catchAllJobs, setCatchAllJobs] = useState<Job[]>([]);
  useEffect(() => {
    setCatchAllJobs([]);
    if (!catchAllId) return;
    let cancelled = false;
    fetchTeamJobs(catchAllId)
      .then((body) => {
        if (!cancelled) setCatchAllJobs(body.engagements.flatMap((group) => group.jobs));
      })
      .catch((err: unknown) => {
        // The chips fall away; the failure is on the record, not silent.
        captureUiError(err, {
          area: "Reviews",
          operation: "read_reviews_team_jobs",
          severity: "warning",
        });
      });
    return () => {
      cancelled = true;
    };
  }, [catchAllId]);
  const reviewLine = catchAllId ? lineOf(catchAllJobs, "reviewer.review_pr") : null;
  // The Reviews team's settings sheet (`teams/TeamSettingsSheet.tsx`), opened at Proctor's section.
  const toSettings = catchAllId
    ? () => navigate(`/desks/${encodeURIComponent(catchAllId)}?settings=proctor`)
    : undefined;

  const ownership = useMemo(
    () => repoOwnershipOf(claims ?? [], list.teams.catchAll),
    [claims, list.teams.catchAll],
  );
  const moveTargets = useMemo(
    () => (desks ?? []).filter((desk) => !list.teams.catchAll.includes(desk.id)),
    [desks, list.teams.catchAll],
  );

  const [panel, setPanel] = useState<"new" | "move" | null>(null);
  const qs = searchParams.toString();
  const hrefFor = useCallback(
    (id: string, params = qs) => `/reviews/${encodeURIComponent(id)}${params ? `?${params}` : ""}`,
    [qs],
  );
  const listHref = qs ? `/reviews?${qs}` : "/reviews";
  const open = (id: string) => {
    setPanel(null);
    navigate(hrefFor(id));
  };

  const changed = useCallback(() => {
    list.refresh();
    setClaimsVersion((n) => n + 1);
  }, [list]);

  const listed = prStateId
    ? ([...list.held.rows, ...list.rest.rows].find((row) => row.prStateId === prStateId) ?? null)
    : null;
  // Next review is the oldest held review that is not this one, as it was
  // before the list was paged: read page one again (the one just answered
  // has left it) under the same search, and open its first other row.
  const hasOtherHeld =
    list.held.rows.some((row) => row.prStateId !== prStateId) ||
    list.held.total > list.held.rows.length;
  const [nextFailed, setNextFailed] = useState(false);
  // The note is about the review that was open; moving on (another review, or
  // back to the list) settles it.
  useEffect(() => setNextFailed(false), [prStateId]);
  const nextReview = async () => {
    invalidateReviews();
    setNextFailed(false);
    try {
      const body = await readReviews(api, { ...query, heldPage: 1 });
      const next = body.held.rows.find((row) => row.prStateId !== prStateId);
      if (!next) {
        navigate(listHref);
        return;
      }
      const params = new URLSearchParams(searchParams);
      params.delete("held");
      setPanel(null);
      navigate(hrefFor(next.prStateId, params.toString()));
    } catch (err) {
      captureUiError(err, {
        area: "Reviews",
        operation: "read_next_held_review",
        severity: "warning",
      });
      // The sheet stays on this review: closing it would read as "nothing else is held".
      setNextFailed(true);
    }
  };
  const deskFor = useCallback(
    (repoFullName: string) => {
      const claim = (claims ?? []).find((entry) => entry.repo === repoFullName.toLowerCase());
      if (!claim?.deskId) return null;
      return {
        deskId: claim.deskId,
        deskName: claim.deskName,
        unowned: list.teams.catchAll.includes(claim.deskId),
      };
    },
    [claims, list.teams.catchAll],
  );

  const sheet =
    panel === "new" ? (
      <NewReviewSheet
        api={api}
        claims={claims ?? []}
        onQueued={(id) => {
          changed();
          open(id);
        }}
        onCancel={() => setPanel(null)}
      />
    ) : panel === "move" ? (
      <MoveReposSheet
        api={api}
        unowned={ownership.unowned}
        teams={moveTargets}
        onMoved={changed}
        onCancel={() => setPanel(null)}
      />
    ) : prStateId ? (
      <ReviewSheet
        key={prStateId}
        api={api}
        prStateId={prStateId}
        deskId={listed?.deskId ?? null}
        deskName={listed?.deskName ?? null}
        unowned={listed?.unowned ?? false}
        deskFor={deskFor}
        onChanged={changed}
        onNextReview={hasOtherHeld ? () => void nextReview() : null}
        onMoveRepo={() => setPanel("move")}
        onClose={() => navigate(listHref)}
        shareUrl={
          typeof window !== "undefined"
            ? `${window.location.origin}/reviews/${encodeURIComponent(prStateId)}`
            : undefined
        }
      />
    ) : null;
  useOwnedSheet(sheet, () => {
    if (panel) setPanel(null);
    else if (prStateId) navigate(listHref);
  });

  const unowned = ownership.unowned.length;
  const sub = [
    claims == null
      ? "Proctor reads every pull request the moment it opens"
      : `Proctor reads every pull request on ${ownership.total} ${ownership.total === 1 ? "repository" : "repositories"} the moment it opens`,
    ownershipSentence(claims, claimsFailed, ownership),
  ].filter(Boolean);

  const filterChip = (
    label: string,
    key: "repo" | "author" | "status",
    value: string,
    options: readonly { value: string; label: string }[],
    allWord: string,
    allLabel: string,
  ) => (
    <MoreMenu
      label={label}
      align="left"
      trigger={({ open: menuOpen, toggle }) => (
        <button
          type="button"
          className={value ? "chip on" : "chip"}
          aria-haspopup="menu"
          aria-expanded={menuOpen}
          onClick={toggle}
        >
          {label}{" "}
          <b>{value ? (options.find((o) => o.value === value)?.label ?? value) : allWord}</b>
          <ChevronDownIcon />
        </button>
      )}
    >
      <MenuItem on={!value} onSelect={() => setParams({ [key]: "" }, true)}>
        {allLabel}
      </MenuItem>
      {options.map((option) => (
        <MenuItem
          key={option.value}
          on={value === option.value}
          onSelect={() => setParams({ [key]: option.value }, true)}
        >
          {option.label}
        </MenuItem>
      ))}
    </MoreMenu>
  );

  const reviewFilter = (reviewLine?.filter ?? {}) as { reviewLevel?: ReviewLevel };
  const chips = (
    <>
      {reviewLine && (
        <>
          <Chip onClick={toSettings}>
            On GitHub <b>{reviewLine.authority === "autonomous" ? "approves" : "would approve"}</b>
          </Chip>
          <Chip onClick={toSettings}>
            Level <b>{LEVEL_WORD[reviewFilter.reviewLevel ?? "balanced"]}</b>
          </Chip>
        </>
      )}
      <form
        className="chip search"
        role="search"
        onSubmit={(event) => {
          event.preventDefault();
          setParams({ q: draft.trim() }, true);
        }}
      >
        <Search width={12} height={12} aria-hidden />
        <input
          aria-label="Search pull requests"
          placeholder="Number or title"
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
        />
        {draft && (
          <button
            type="button"
            className="x"
            aria-label="Clear search"
            onClick={() => {
              setDraft("");
              setParams({ q: "" }, true);
            }}
          >
            ×
          </button>
        )}
      </form>
      {filterChip("Status", "status", query.status, REVIEW_STATUS_MENU, "any", "Any status")}
      {filterChip(
        "Repository",
        "repo",
        query.repo,
        list.repos.map((repo) => ({ value: repo, label: repoName(repo) })),
        "all",
        "Every repository",
      )}
      {filterChip(
        "Author",
        "author",
        query.author,
        list.authors.map((author) => ({ value: author, label: author })),
        "anyone",
        "Anyone",
      )}
    </>
  );

  const nothingReviews = list.ready && list.teams.reviewing.length === 0;
  const loading = !list.ready;
  const searching = Boolean(query.q || query.repo || query.author);

  const footRef = useRef<HTMLDivElement>(null);
  const toRestPage = (page: number, fromFoot = false) => {
    setParams({ page: String(page) }, false);
    if (fromFoot) footRef.current?.closest(".center")?.scrollTo?.({ top: 0 });
  };

  const row = (entry: ReviewRow) => {
    const state = listRowState(entry);
    return (
      <Row
        key={entry.prStateId}
        glyph={
          <span
            className={state.dot.mode ? `sdot ${state.dot.mode}` : "sdot"}
            style={{ "--c": state.dot.color } as CSSProperties}
            aria-hidden
          />
        }
        rowKey={`#${entry.prNumber}`}
        title={entry.title ?? `Pull request #${entry.prNumber}`}
        tag={{ label: repoName(entry.repoFullName) }}
        state={
          <>
            <span className="state-word" style={{ "--sw": state.color } as CSSProperties}>
              {state.word}
            </span>
            <span className="sep">·</span>
            {reviewStateLine(entry)}
          </>
        }
        when={reviewAge(entry, now)}
        verb={state.verb}
        sel={!panel && entry.prStateId === prStateId}
        onOpen={() => open(entry.prStateId)}
      />
    );
  };

  const restPager = (fromFoot: boolean) =>
    loading || list.failed ? null : (
      <Pager
        page={query.page}
        pageSize={list.rest.pageSize}
        total={list.rest.total}
        onPage={(page) => toRestPage(page, fromFoot)}
        label="pull requests"
      />
    );

  return (
    <div className="wsurf" data-testid="reviews-surface">
      <ListHead
        title="Pull request reviews"
        sub={sub}
        acts={
          <>
            {unowned > 0 && (
              <Btn
                variant="primary"
                size="sm"
                on={panel === "move"}
                onClick={() => setPanel("move")}
                data-testid="reviews-move-open"
              >
                Move repositories onto Teams
              </Btn>
            )}
            <Btn
              variant="secondary"
              size="sm"
              on={panel === "new"}
              onClick={() => setPanel("new")}
              data-testid="reviews-new-open"
            >
              Review a pull request
            </Btn>
            {toSettings && (
              <Btn
                icon
                aria-label="Review settings for unowned repositories"
                title="Review settings for unowned repositories"
                onClick={toSettings}
              >
                <Settings width={16} height={16} aria-hidden />
              </Btn>
            )}
          </>
        }
        chips={chips}
        picked={
          !loading && list.reviewsToday > 0 ? (
            <>
              <b>{list.reviewsToday}</b> {list.reviewsToday === 1 ? "review" : "reviews"} today
            </>
          ) : undefined
        }
      />
      <Groups>
        {list.failed && (
          <div className="qnote err" role="alert">
            <span>Could not read the pull requests just now.</span>
          </div>
        )}
        {nextFailed && (
          <div className="qnote err" role="alert">
            <span>Could not open the next held review just now. Try Next review again.</span>
          </div>
        )}
        {nothingReviews ? (
          <div className="empty dashed" data-testid="reviews-empty">
            <h3>No Team reviews pull requests yet</h3>
            <p>
              Create a Team at the Pull request reviews size, or put Proctor on a Team that already
              works on your repositories.
            </p>
          </div>
        ) : (
          <>
            <Group
              label="Held for you"
              tone="needs"
              count={loading ? 0 : list.held.total}
              empty={emptyLine(loading, searching, {
                filtered: "No held review matches.",
                none: "Nothing is held. Proctor posts on its own at this level.",
              })}
              aside={
                loading || list.failed ? null : (
                  <Pager
                    page={query.heldPage}
                    pageSize={list.held.pageSize}
                    total={list.held.total}
                    onPage={(page) => setParams({ held: String(page) }, false)}
                    label="held reviews"
                  />
                )
              }
            >
              {list.held.rows.map(row)}
            </Group>
            <Group
              label="All pull requests"
              count={loading ? 0 : list.rest.total}
              empty={emptyLine(loading, searching || Boolean(query.status), {
                filtered: "No pull request matches. Clear the search or a filter to see more.",
                none: "Proctor has not seen a pull request on these repositories yet.",
              })}
              aside={restPager(false)}
              foot={
                list.rest.total > list.rest.pageSize && !loading && !list.failed ? (
                  <div className="pagefoot" ref={footRef}>
                    {restPager(true)}
                  </div>
                ) : null
              }
            >
              {list.rest.rows.map(row)}
            </Group>
          </>
        )}
      </Groups>
    </div>
  );
}

export function ReviewsRoute() {
  return <ReviewsPage />;
}
